import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";
import { useAuth } from "./auth";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** True when the failure was a 401 (signed out / session expired). */
  unauthorized: boolean;
}

/**
 * Run an async API call on mount (and whenever `deps` change), tracking
 * loading/error/data. On a 401 it flips the shared auth state to anonymous (so the
 * app routes to sign-in) and marks `unauthorized`. The fetcher receives an
 * AbortSignal so an in-flight request is cancelled when deps change/unmount.
 */
export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): AsyncState<T> & { reload: () => void } {
  const { signOut } = useAuth();
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
    unauthorized: false,
  });
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null, unauthorized: false }));
    fetcherRef
      .current(ctrl.signal)
      .then((data) => {
        if (!ctrl.signal.aborted) {
          setState({ data, loading: false, error: null, unauthorized: false });
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) {
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          setState({ data: null, loading: false, error: null, unauthorized: true });
          return;
        }
        const message = err instanceof Error ? err.message : "Something went wrong.";
        setState({ data: null, loading: false, error: message, unauthorized: false });
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Helper for one-shot mutations (submit a ticket, etc.) with consistent 401
 * handling and pending/error state.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) {
  const { signOut } = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          setError("Your session has expired. Please sign in again.");
          return undefined;
        }
        setError(err instanceof Error ? err.message : "Something went wrong.");
        return undefined;
      } finally {
        setPending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fn],
  );

  return { run, pending, error, setError };
}
